/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { useAuth0 } from '@auth0/auth0-react'
import { t } from '@lingui/macro'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography
} from '@mui/material'
import { CreateCarePartnerButton, CreateCarePartnerModal } from '@om1/aspen-api'
import { CarePartnersService, OpenAPI } from '@om1/aspen-api/codegen/client'
import { CarePartnerResponseDTO } from '@om1/aspen-api/codegen/client/models/CarePartnerResponseDTO'
import { CarePartnerUpdateDTO } from '@om1/aspen-api/codegen/client/models/CarePartnerUpdateDTO'
import { useCarePartners } from '@om1/aspen-api/codegen/hooks/useCarePartners'
import { formatDate, InlineDropdown, InlineDropdownOption } from '@om1/platform-components'
import OmniDataGridTable, { ProColumns } from '@om1/platform-components/OmniDataGridTable'
import { Download, MessageSquare } from 'lucide-react'
import { useSnackbar } from 'notistack'
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CopyableIdentifier } from '../components/CopyableIdentifier'
import { FilterIconButton } from '../components/FilterIconButton'
import { DrawerComponent } from '../components/GlobalDrawer'
import LogAttemptAction from '../components/LogAttemptAction'
import { StableSearchInput } from '../components/StableSearchInput'
import { StatusBadge } from '../components/StatusBadge'
import { filterAllocatedStatusOptions } from '../utils/candidate-status'
import { useListUrlState } from '../hooks/useListUrlState'

/**
 * Get all available care partner status options with labels
 * Note: "candidate" is excluded from filter options, "in_contact" is included
 */
const getCarePartnerStatusOptions = (): InlineDropdownOption<string>[] => [
    { value: 'in_contact', label: t`In Contact` },
    { value: 'registered', label: t`Registered` },
    { value: 'allocated', label: t`Allocated` },
    { value: 'allocated_group_2', label: t`Allocated Group 2` },
    { value: 'allocated_group_3', label: t`Allocated Group 3` },
    { value: 'record_retrieval', label: t`Record Retrieval` },
    { value: 'undecided', label: t`Undecided` },
    { value: 'declined', label: t`Declined` },
    { value: 'unreachable', label: t`Unreachable` },
    { value: 'duplicate', label: t`Duplicate` }
]

/**
 * Get all status options including "in_contact" for inline dropdown (status updates)
 */
const getAllCarePartnerStatusOptions = (): InlineDropdownOption<string>[] => [
    { value: 'candidate', label: t`Candidate` },
    { value: 'in_contact', label: t`In Contact` },
    { value: 'registered', label: t`Registered` },
    { value: 'allocated', label: t`Allocated` },
    { value: 'allocated_group_2', label: t`Allocated Group 2` },
    { value: 'allocated_group_3', label: t`Allocated Group 3` },
    { value: 'record_retrieval', label: t`Record Retrieval` },
    { value: 'undecided', label: t`Undecided` },
    { value: 'declined', label: t`Declined` },
    { value: 'unreachable', label: t`Unreachable` },
    { value: 'duplicate', label: t`Duplicate` }
]

const CarePartnersPage: React.FC = () => {
    const { user } = useAuth0()
    const studySlug = (user?.org_name as string | undefined) ?? ''
    const history = useHistory()
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [selectedCarePartner, setSelectedCarePartner] = useState<CarePartnerResponseDTO | null>(null)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)

    // Create initial filters for care partners
    // Note: Organization filtering is handled by RLS, no need for studyName filter
    const initialFilters = useMemo(() => ({}), [])

    // List state lives in the URL (layered over localStorage) so clicking into
    // a care partner and coming back — or sharing the link — preserves it.
    const listState = useListUrlState('care-partners', {
        page: 1,
        limit: 10,
        sort: 'first_name:asc',
        search: '',
        filters: initialFilters
    })

    // Fetch care partners data
    const carePartnersData = useCarePartners(1, 10, 'first_name:asc', initialFilters, listState)

    // The filter map stores status comma-joined; the Select needs a list.
    const selectedCarePartnerStatuses = useMemo(
        () => (carePartnersData.filters?.status ? carePartnersData.filters.status.split(',').filter(Boolean) : []),
        [carePartnersData.filters?.status]
    )

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return Boolean((carePartnersData.search && carePartnersData.search.length > 0) || carePartnersData.filters?.status)
    }, [carePartnersData.search, carePartnersData.filters?.status])

    // CSV Export
    const { enqueueSnackbar } = useSnackbar()
    const [isExporting, setIsExporting] = useState(false)

    const handleExportCsv = useCallback(async () => {
        setIsExporting(true)
        try {
            const params = new URLSearchParams()
            const filters = carePartnersData.filters || {}

            // Repeat the param per status so the CSV matches the worklist.
            if (filters.status) {
                filters.status
                    .split(',')
                    .filter(Boolean)
                    .forEach((status) => params.append('status', status))
            }
            if (filters.firstName) params.append('first_name', filters.firstName)
            if (filters.lastName) params.append('last_name', filters.lastName)
            if (filters.email) params.append('email', filters.email)
            if (carePartnersData.search) params.append('search', carePartnersData.search)
            if (carePartnersData.sort) params.append('sort', carePartnersData.sort)

            const queryString = params.toString()
            const url = `${OpenAPI.BASE}/aspen/care-partners/export${queryString ? `?${queryString}` : ''}`

            const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : await OpenAPI.TOKEN?.({ method: 'GET', url })
            const response = await fetch(url, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
            })

            if (!response.ok) {
                throw new Error(`Export failed: ${response.statusText}`)
            }

            const blob = await response.blob()
            const contentDisposition = response.headers.get('content-disposition')
            const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/)
            const filename = filenameMatch?.[1] || `care_partner_export_${new Date().toISOString().slice(0, 10)}.csv`

            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = filename
            a.click()
            URL.revokeObjectURL(a.href)

            enqueueSnackbar(t`CSV export downloaded successfully`, { variant: 'success' })
        } catch (error) {
            console.error('CSV export failed:', error)
            enqueueSnackbar(t`Failed to export CSV. Please try again.`, { variant: 'error' })
        } finally {
            setIsExporting(false)
        }
    }, [carePartnersData.filters, carePartnersData.search, carePartnersData.sort, enqueueSnackbar])

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false)
        setSelectedCarePartner(null)
    }

    const handleCarePartnerSuccess = () => {
        handleCloseEditModal()
        carePartnersData.refetch?.()
    }

    // Update care partner status
    const handleStatusUpdate = async (carePartnerId: string, newStatus: NonNullable<CarePartnerUpdateDTO['status']>) => {
        try {
            await CarePartnersService.updateCarePartnerStatusCarePartnersCarePartnerIdStatusPatch({
                carePartnerId,
                newStatus
            })
            carePartnersData.refetch?.()
        } catch (error) {
            console.error('Failed to update care partner status:', error)
        }
    }

    // Define ProColumns for the care partners data grid
    const proTableCarePartnerColumns: ProColumns<CarePartnerResponseDTO>[] = [
        {
            title: t`Identifier`,
            dataIndex: 'friendlyId',
            key: 'friendlyId',
            width: 150,
            render: (_: unknown, carePartner: CarePartnerResponseDTO) => <CopyableIdentifier identifier={carePartner.friendlyId} />
        },
        {
            title: t`First Name`,
            dataIndex: 'firstName',
            key: 'firstName',
            width: 150,
            sorter: true,
            render: (text) => (
                <div className='flex flex-col gap-1'>
                    <span className='font-medium'>{text}</span>
                </div>
            )
        },
        {
            title: t`Last Name`,
            dataIndex: 'lastName',
            key: 'lastName',
            width: 150,
            sorter: true
        },
        {
            title: t`Date of Birth`,
            dataIndex: 'dateOfBirth',
            key: 'dateOfBirth',
            width: 120,
            sorter: true,
            render: (text: any, record: CarePartnerResponseDTO) => {
                return formatDate(record.dateOfBirth)
            }
        },
        {
            title: t`Phone`,
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 140,
            render: (text, carePartner) => (
                <div className='flex flex-col gap-1'>
                    <span className='flex items-center gap-1'>
                        {text} {carePartner.smsConsent && <MessageSquare className='w-3.5 h-3.5 text-green-600' />}
                    </span>
                    {/* Language Preference */}
                    {carePartner.languagePreference && (
                        <div className='flex items-center gap-1 text-[10px] leading-none mt-1'>
                            <span className='text-gray-600'>{t`Lang:`}</span>
                            <span
                                className={`px-1 py-0.5 rounded text-[6px] font-medium ${
                                    carePartner.languagePreference === 'es' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                                }`}
                            >
                                {carePartner.languagePreference === 'es' ? 'ES' : 'EN'}
                            </span>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: t`Email`,
            dataIndex: 'email',
            key: 'email',
            width: 200
        },
        {
            title: t`Created At`,
            dataIndex: 'createdDttm',
            key: 'createdDttm',
            width: 150,
            sorter: true,
            render: (text: any) => {
                if (!text || typeof text !== 'string') return '-'
                try {
                    const date = new Date(text)
                    if (isNaN(date.getTime())) return '-'
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })
                } catch {
                    return '-'
                }
            }
        },
        {
            title: t`Updated At`,
            dataIndex: 'updatedDttm',
            key: 'updatedDttm',
            width: 150,
            sorter: true,
            render: (text: any) => {
                if (!text || typeof text !== 'string') return '-'
                try {
                    const date = new Date(text)
                    if (isNaN(date.getTime())) return '-'
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })
                } catch {
                    return '-'
                }
            }
        },
        {
            title: t`Status`,
            key: 'status',
            width: 180,
            render: (_, carePartner) => {
                const status = carePartner.status || 'candidate'
                // Show null/empty when status is "candidate"
                if (status === 'candidate') {
                    return (
                        <InlineDropdown
                            value={status}
                            options={filterAllocatedStatusOptions(getAllCarePartnerStatusOptions(), studySlug)}
                            onSelect={async (newStatus) => {
                                await handleStatusUpdate(carePartner.id, newStatus as NonNullable<CarePartnerUpdateDTO['status']>)
                            }}
                            renderTrigger={(value, isOpen) => (
                                <div className='inline-flex items-center cursor-pointer hover:opacity-80'>
                                    <span className={`ml-1 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                                </div>
                            )}
                        />
                    )
                }
                return (
                    <InlineDropdown
                        value={status}
                        options={filterAllocatedStatusOptions(getAllCarePartnerStatusOptions(), studySlug)}
                        onSelect={async (newStatus) => {
                            await handleStatusUpdate(carePartner.id, newStatus as NonNullable<CarePartnerUpdateDTO['status']>)
                        }}
                        renderTrigger={(value, isOpen) => (
                            <div className='inline-flex items-center cursor-pointer hover:opacity-80'>
                                <StatusBadge status={value} />
                                <span className={`ml-1 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                            </div>
                        )}
                    />
                )
            }
        },
        {
            title: t`Log Attempt`,
            key: 'logAttempt',
            width: 120,
            align: 'center',
            render: (_, carePartner) => (
                <div className='flex items-center justify-center gap-2'>
                    <LogAttemptAction
                        entityName={`${carePartner.firstName} ${carePartner.lastName}`}
                        entityId={carePartner.id}
                        entityType='care-partner'
                    />
                    <span className='font-medium text-gray-900'>{carePartner.contactLogCount || 0}</span>
                </div>
            )
        },
        {
            title: t`Actions`,
            key: 'actions',
            width: 120,
            align: 'center',
            render: (_, carePartner) => (
                <div className='flex items-center gap-2'>
                    <Button variant='outlined' size='small' onClick={() => history.push(`/carepartners/${carePartner.id}`)}>
                        {t`View`}
                    </Button>
                </div>
            )
        }
    ]

    return (
        <div className='h-[90vh] flex flex-col'>
            {/* Header */}
            <div className='flex-shrink-0 p-6 pb-4 border-b'>
                <div className='flex justify-between items-center mb-6'>
                    <Typography variant='h4'>{t`Care Partners`}</Typography>

                    <div className='flex items-center gap-4 flex-wrap'>
                        <Button
                            variant='outlined'
                            size='small'
                            startIcon={isExporting ? <CircularProgress size={16} /> : <Download size={16} />}
                            onClick={handleExportCsv}
                            disabled={isExporting}
                        >
                            {isExporting ? t`Exporting...` : t`Export CSV`}
                        </Button>
                        <FilterIconButton onClick={() => setIsDrawerOpen(true)} hasActiveFilters={hasActiveFilters} />
                        <CreateCarePartnerButton
                            onSuccess={(carePartnerId) => {
                                carePartnersData.refetch?.()
                            }}
                            variant='outlined'
                            size='small'
                        >
                            {t`Create Care Partner`}
                        </CreateCarePartnerButton>
                    </div>
                </div>

                {/* Data Grid */}
                <div className='pb-12'>
                    <OmniDataGridTable<CarePartnerResponseDTO>
                        dataSource={carePartnersData.response?.data || []}
                        columns={proTableCarePartnerColumns}
                        loading={carePartnersData.loading}
                        currentPage={carePartnersData.page}
                        pageSize={carePartnersData.limit}
                        total={carePartnersData.response?.meta?.totalItems || 0}
                        onPaginationChange={(page, pageSize) => {
                            carePartnersData.setPage(page)
                            carePartnersData.setLimit(pageSize)
                        }}
                        onSortChange={(sortString) => {
                            carePartnersData.setSort(sortString ?? '')
                        }}
                        rowKey='id'
                        enableColumnStatePersistence={true}
                        storageKey='care-partners-list'
                    />
                </div>
            </div>

            {/* Global Drawer for Filters */}
            <DrawerComponent open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={t`Filters`} width={420}>
                <div className='flex flex-col gap-6 p-4'>
                    {/* Search */}
                    <div className='flex flex-col gap-2'>
                        <Typography variant='subtitle2' className='font-semibold'>
                            {t`Search`}
                        </Typography>
                        <StableSearchInput
                            onSearchChange={carePartnersData.setSearch}
                            initialValue={carePartnersData.search}
                            placeholder={t`Search by name, email, or identifier`}
                        />
                        <Typography variant='caption' className='text-gray-500'>
                            {t`Search across name, email, and phone fields`}
                        </Typography>
                    </div>

                    {/* Divider */}
                    <div className='border-t border-gray-200' />

                    {/* Status Filter */}
                    <div className='flex flex-col gap-2'>
                        <Typography variant='subtitle2' className='font-semibold'>
                            {t`Status`}
                        </Typography>
                        <FormControl
                            fullWidth
                            size='small'
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: 'white'
                                }
                            }}
                        >
                            <InputLabel id='status-filter-label'>{t`Filter Status`}</InputLabel>
                            <Select
                                multiple
                                id='status-filter-select'
                                labelId='status-filter-label'
                                label={t`Filter Status`}
                                inputProps={{ 'data-testid': 'care-partner-status-filter-input' }}
                                value={selectedCarePartnerStatuses}
                                onChange={(event: SelectChangeEvent<string[]>) => {
                                    // MUI hands back a string when a native select
                                    // fires; normalise so we only deal with a list.
                                    const value = event.target.value
                                    const newStatuses = typeof value === 'string' ? value.split(',').filter(Boolean) : value
                                    const updatedFilters: Record<string, string> = {
                                        ...carePartnersData.filters
                                    }
                                    // Comma-joined because the filter map is
                                    // Record<string, string>; useCarePartners
                                    // expands it into repeated ?status= params.
                                    if (newStatuses.length > 0) {
                                        updatedFilters.status = newStatuses.join(',')
                                    } else {
                                        delete updatedFilters.status
                                    }
                                    carePartnersData.setFilters(updatedFilters)
                                }}
                                renderValue={(selected) =>
                                    selected.length === 0 ? (
                                        <em>{t`All`}</em>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((status) => (
                                                <StatusBadge key={status} status={status} />
                                            ))}
                                        </Box>
                                    )
                                }
                            >
                                {filterAllocatedStatusOptions(getCarePartnerStatusOptions(), studySlug).map((option) => (
                                    <MenuItem key={option.value} value={option.value} data-testid={`care-partner-status-option-${option.value}`}>
                                        <Checkbox checked={selectedCarePartnerStatuses.includes(option.value)} size='small' />
                                        <ListItemText disableTypography primary={<StatusBadge status={option.value} />} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>
            </DrawerComponent>

            {/* Edit Modal */}
            <CreateCarePartnerModal
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
                onSuccess={handleCarePartnerSuccess}
                mode='edit'
                carePartner={selectedCarePartner}
            />
        </div>
    )
}

export default CarePartnersPage
